// ============================================================================
// OBT — Yayınlanmış kampanyanın cevap son tarihini UZATMA diyaloğu
// ============================================================================
// Backend sözleşmesi: POST /obt/campaigns/{id}/extend-due (ExtendDueIn.new_due_at).
// Kurallar backend'de fail-closed uygulanır (talimat §3.5 / service.py):
//   - Yalnız `collecting_responses` durumundaki kampanyada,
//   - Yalnız İLERİ tarih (mevcut son tarih kısaltılamaz),
//   - Son tarihi olmayan kampanyaya son tarih EKLENEMEZ,
//   - Yetki yayın-anı immutable kaydından (yayınlayan ya da oluşturan).
// Bu diyalog yalnız UX kolaylığıdır; nihai yetki/kural kontrolü sunucudadır.
// `new_due_at`, CampaignBuilder ile AYNI biçimde gönderilir: ham datetime-local
// (naive) değeri — backend yorumlar. Böylece "ileri tarih" karşılaştırması
// oluşturma akışıyla tutarlı kalır.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { extendCampaignDue } from './campaignApi';
import { normalizeObtError } from '../obtErrors';

/** ISO/tarih → <input type="datetime-local"> değeri (yerel, saniyesiz). */
function toLocalInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {{campaign_id:string, title?:string, response_due_at?:string}|null} props.campaign
 * @param {() => void} props.onClose
 * @param {() => void} [props.onExtended]  Başarı sonrası (liste yenileme vb.)
 */
export default function ExtendDueDialog({ open, campaign, onClose, onExtended }) {
  const currentDue = campaign?.response_due_at || null;
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setValue(toLocalInput(currentDue));
      setSubmitting(false);
      setError(null);
    }
  }, [open, currentDue]);

  const isSetting = !currentDue; // tarih YOK → "belirle"; VAR → "uzat"
  const currentLocal = toLocalInput(currentDue);
  const nowLocal = toLocalInput(Date.now());
  // İstemci-tarafı kontroller (UX). Nihai doğrulama backend'de.
  const notForward = Boolean(value) && Boolean(currentLocal) && value <= currentLocal;
  const notFuture = isSetting && Boolean(value) && value <= nowLocal;
  const canSubmit = Boolean(value) && !notForward && !notFuture && !submitting;

  const handleSubmit = async () => {
    if (!campaign?.campaign_id || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await extendCampaignDue(campaign.campaign_id, value);
      if (onExtended) onExtended();
      onClose();
    } catch (e) {
      setError(normalizeObtError(e));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isSetting ? 'Son cevap tarihi belirle' : 'Cevap süresini uzat'}
          </DialogTitle>
          <DialogDescription>
            {campaign?.title ? `“${campaign.title}” bilgi talebi` : 'Bilgi talebi'} için{' '}
            {isSetting
              ? 'bir son cevap tarihi belirleyin (kurulumda boş bırakılmış). Tarih gelecekte olmalıdır.'
              : 'yeni cevap son tarihi belirleyin. Süre yalnız ileri tarihe uzatılabilir.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {currentDue && (
            <p className="text-xs text-gray-500">
              Mevcut son tarih:{' '}
              {new Date(currentDue).toLocaleString('tr-TR', {
                timeZone: 'Europe/Istanbul',
              })}
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="obt-extend-due">Yeni son tarih</Label>
            <Input
              id="obt-extend-due"
              type="datetime-local"
              value={value}
              min={currentLocal || nowLocal || undefined}
              disabled={submitting}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          {notForward && (
            <p className="text-xs text-red-600">
              Yeni tarih mevcut son tarihten sonra olmalı (süre kısaltılamaz).
            </p>
          )}
          {notFuture && (
            <p className="text-xs text-red-600">
              Son cevap tarihi gelecekte olmalıdır.
            </p>
          )}
          {error && (
            <p className="text-xs text-red-600" role="alert">
              {error.message || 'Süre uzatılamadı.'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Vazgeç
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {isSetting ? 'Tarihi belirle' : 'Süreyi uzat'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
